import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { trackNavCtaClick } from '../lib/analytics';
import { BRAND_LOGO } from '../constants/brand';

/**
 * Mundalo Header
 *
 * Spec (Feb 2026 mobile rework):
 * - Sticky navbar - ALWAYS solid (never transparent), readable at all times on every device.
 * - Default state: white #FFFFFF background, Deep Ocean #0A1F2E text.
 * - After scrolling past 80px: Canvas #F9F6EF background with subtle shadow.
 * - Mobile (<768px): hamburger icon on the right opens a full-width white dropdown
 *   (How It Works · Pricing · Relo News · Sign In · Start Free CTA).
 *   Tap outside or the X icon to close. iOS-Safari tested.
 */
const Header = ({ currentPage = '' }) => {
  const { language, setLanguage } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // iter 125 + 129 - Routes where the nav CTA mirrors the hero CTA ("Get
  // Your Blueprint" → /relocation/blueprint). On every other route the
  // persistent CTA stays "Start Free" because it makes sense from
  // Community / Classroom / etc.
  const blueprintRoutes = new Set(['/', '/relocation/blueprint']);
  const isHomepage = blueprintRoutes.has(location.pathname);
  const ctaLabel = isHomepage ? 'Get Your Blueprint' : 'Start free - 15 days';
  const ctaHref  = isHomepage ? '/relocation/blueprint' : '/start';
  const ctaSource = isHomepage ? 'homepage_blueprint_cta' : 'other_page_start_free_cta';
  const handleCtaClick = () => trackNavCtaClick({ source: ctaSource });

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile menu is open (mobile Safari friendly)
  useEffect(() => {
    if (mobileMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileMenuOpen]);

  const onHome = location.pathname === '/';

  // iter 187.j - hash links MUST route through React Router `<Link>`
  // (not raw <a href>). Native <a href="#pricing"> clicks don't
  // trigger useLocation() updates, so our smart scroll-with-offset
  // logic in ScrollToTop.js never fires and the sticky header covers
  // the anchor. Passing `{ pathname, hash }` to <Link> makes React
  // Router update state on same-page hash navigation.
  const navLinks = [
    { to: { pathname: '/', hash: '#how' }, label: 'How It Works', key: 'how' },
    { to: '/about', label: 'About', key: 'about' },
    { to: { pathname: '/', hash: '#pricing' }, label: 'Pricing', key: 'pricing' },
    { to: '/adventures', label: 'Adventures', key: 'adventures' },
    { to: '/relo-news', label: 'Relo News', key: 'news' },
    { to: '/become-a-partner', label: 'Become a Partner', key: 'become-partner' },
    { to: '/portal', label: 'Sign In', key: 'signin' },
    // iter 186.d - "Already Paid?" removed from top-level nav. Recovery
    // is now surfaced contextually inside PortalLogin.js (see the
    // `link-recover-paid-account` button on the login form). Frees up
    // one slot in the horizontal nav for paid-ad landing pages where
    // every millisecond of scan-to-CTA matters.
  ];

  // On home, the navbar overlays the hero image - keep it transparent + white
  // text at the top of the page. Once user scrolls past the hero (80px), it
  // becomes the solid canvas-color navbar with dark text. On every other
  // page, the navbar is always solid + dark text.
  const overlayHero = onHome && !scrolled;

  const headerBgClass = overlayHero
    ? 'bg-transparent border-b border-transparent'
    : scrolled
    ? 'bg-[#F9F6EF] border-b border-[#0A1F2E]/8 shadow-[0_2px_12px_rgba(10,31,46,0.06)]'
    : 'bg-[#FFFFFF] border-b border-[#0A1F2E]/8';

  const textColorClass = overlayHero ? 'text-white' : 'text-[#0A1F2E]';
  // iter 144e - consistent letter-spacing + font-weight across ALL nav
  // items (including the CTA pill). `inline-flex items-center leading-none
  // whitespace-nowrap` pins every link's vertical baseline to the row
  // centerline AND prevents multi-word labels like "HOW IT WORKS" or
  // "BECOME A PARTNER" from wrapping at narrow widths.
  const linkBaseClass = overlayHero
    ? 'inline-flex items-center leading-none whitespace-nowrap font-mono font-normal uppercase text-[11px] tracking-[0.16em] text-white/90 hover:text-white transition-colors'
    : 'inline-flex items-center leading-none whitespace-nowrap font-mono font-normal uppercase text-[11px] tracking-[0.16em] text-[#0A1F2E]/80 hover:text-[#2CA8A0] transition-colors';

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-colors duration-300 ${headerBgClass}`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 xl:h-20">
          {/* iter 144o - Official Mundalo wordmark.
              • overlayHero (over dark hero image) → white version
              • otherwise (light cream/white nav)  → black version */}
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center flex-shrink-0 pr-3"
            aria-label="Mundalo - Home"
          >
            <img
              src={overlayHero ? BRAND_LOGO.white : BRAND_LOGO.black}
              alt="Mundalo"
              className="block h-9 xl:h-10 w-[180px] xl:w-[200px] object-contain object-left bg-transparent"
              style={overlayHero ? {
                // iter 144p - subtle multi-layer shadow so the white wordmark
                // stays crisp on any hero photograph (light skies, light walls, etc.)
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.55)) drop-shadow(0 0 10px rgba(0,0,0,0.35))',
              } : undefined}
              loading="eager"
              decoding="sync"
            />
          </Link>

          {/* Desktop Nav - iter 187.m: raised breakpoint md → xl (1280).
              Below 1280 the seven uppercase nav links + logo + CTA + lang
              selector cannot fit inside the viewport; users at 1024-1279
              (common laptop / iPad landscape) now get the hamburger, which
              already contains every link + the language toggle. The gap
              scale + logo width now key off xl so nothing overflows. */}
          <nav className="hidden xl:flex items-center gap-5 2xl:gap-8 ml-6 2xl:ml-10" aria-label="Primary">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.to}
                data-testid={`nav-${link.key}`}
                className={`${linkBaseClass} ${currentPage === link.key ? 'text-[#2CA8A0]' : ''}`}
              >
                {link.label}
              </Link>
            ))}

            {/* iter 125 - CTA is homepage-aware: "Get Your Blueprint" on /,
                "Start Free" everywhere else. iter 144e: tracking + leading
                normalized to match the other nav links so the row reads
                consistent. iter 187.i: horizontal padding trimmed
                px-5 → px-4 (4px per side) so the CTA fits cleanly at
                narrower desktop widths without shrinking font-size,
                weight, tracking, or background. */}
            <Link
              to={ctaHref}
              data-testid="nav-cta-start-free"
              onClick={handleCtaClick}
              className="inline-flex items-center leading-none whitespace-nowrap font-mono uppercase text-[11px] tracking-[0.16em] font-semibold bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] px-4 py-2.5 rounded-full transition-all hover:-translate-y-px shadow-sm"
            >
              {ctaLabel}
            </Link>

            {/* Language Selector - iter 107: keep option text dark even
                when the trigger is on a hero overlay (text-white on
                <select> was bleeding into the dropdown so the ES option
                rendered as white-on-white). iter 187.m: compact width
                (px-1.5, tighter tracking) so the row fits at xl=1280. */}
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={`text-xs font-mono uppercase tracking-wider bg-transparent cursor-pointer px-1.5 py-1 rounded ${
                overlayHero
                  ? 'border border-white/40 text-white'
                  : 'border border-[#0A1F2E]/15 text-[#0A1F2E]'
              }`}
              data-testid="language-selector"
              aria-label="Language"
            >
              <option value="en" style={{ color: '#0A1F2E', backgroundColor: '#F9F6EF' }}>EN</option>
              <option value="es" style={{ color: '#0A1F2E', backgroundColor: '#F9F6EF' }}>ES</option>
            </select>
          </nav>

          {/* Mobile / laptop hamburger - iter 187.m: now covers every
              viewport below xl (1280), not just below md. */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((v) => !v)}
            className={`xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
              overlayHero
                ? 'text-white hover:bg-white/10'
                : 'text-[#0A1F2E] hover:bg-[#0A1F2E]/5'
            }`}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-nav"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu - slide down panel */}
        <div
          id="mobile-nav"
          data-testid="mobile-nav"
          className={`xl:hidden overflow-hidden bg-[#FFFFFF] border-t border-[#0A1F2E]/8 transition-[max-height,opacity] duration-300 ease-out ${
            mobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
          }`}
          aria-hidden={!mobileMenuOpen}
        >
          <nav className="px-6 py-6 flex flex-col gap-1" aria-label="Mobile">
            {navLinks.map((link) => (
              <Link
                key={link.key}
                to={link.to}
                data-testid={`mobile-nav-${link.key}`}
                onClick={() => setMobileMenuOpen(false)}
                className="font-mono uppercase text-xs tracking-[0.15em] text-[#0A1F2E] hover:text-[#2CA8A0] py-3 border-b border-[#0A1F2E]/8"
              >
                {link.label}
              </Link>
            ))}

            <Link
              to={ctaHref}
              data-testid="mobile-nav-cta-start-free"
              onClick={() => { handleCtaClick(); setMobileMenuOpen(false); }}
              className="mt-4 text-center font-mono uppercase text-xs tracking-[0.15em] font-semibold bg-[#E8A830] text-[#0A1F2E] py-3.5 rounded-full shadow-sm"
            >
              {ctaLabel}
            </Link>

            <div className="pt-5 flex items-center justify-between">
              <span className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60">
                Language
              </span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="text-xs font-mono uppercase tracking-wider border border-[#0A1F2E]/15 bg-transparent px-2 py-1 rounded text-[#0A1F2E]"
                data-testid="mobile-language-selector"
              >
                <option value="en">EN</option>
                <option value="es">ES</option>
              </select>
            </div>
          </nav>
        </div>
      </header>

      {/* Tap-outside backdrop for mobile menu */}
      {mobileMenuOpen && (
        <button
          type="button"
          aria-label="Close menu"
          data-testid="mobile-menu-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          className="xl:hidden fixed inset-0 top-16 z-40 bg-[#0A1F2E]/30 backdrop-blur-[2px]"
        />
      )}
    </>
  );
};

export default Header;
